import { useState } from "react";
import { Modal, BlockStack } from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { showToast } from "@shared/lib";
import { FAQItem } from "@features/events/types";
import { GenerativeTextField } from "@shared/ui";

interface AddEditFAQModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (faqItem: FAQItem) => void;
  editingItem?: FAQItem | null;
}

export default function AddEditFAQModal({
  isOpen,
  onClose,
  onSave,
  editingItem = null,
}: AddEditFAQModalProps) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [formData, setFormData] = useState<FAQItem>(() =>
    editingItem
      ? {
          question: { ...editingItem.question },
          answer: { ...editingItem.answer },
        }
      : {
          question: {},
          answer: {},
        }
  );
  const [isQuestionValid, setIsQuestionValid] = useState(true);
  const [isAnswerValid, setIsAnswerValid] = useState(true);

  const validateForm = (): boolean => {
    const newErrors: { question?: string; answer?: string } = {};

    // Check if at least one language has content
    if (!Object.values(formData.question).some((value) => value.trim())) {
      newErrors.question = t("common.requiredField", {
        field_name: `${t("eventDetails.faq.modal.questionSection")}`,
      });
    }

    if (!Object.values(formData.answer).some((value) => value.trim())) {
      newErrors.answer = t("common.requiredField", {
        field_name: `${t("eventDetails.faq.modal.answerSection")}`,
      });
    }

    // Return true only if there are no errors AND all language validations pass
    return Object.keys(newErrors).length === 0 && isQuestionValid && isAnswerValid;
  };

  const handleSave = () => {
    if (validateForm()) {
      onSave(formData);
      onClose();
    } else {
      // Show toast error when validation fails
      dispatch(
        showToast({
          message: t("eventDetails.faq.modal.error_missing_fields"),
          type: "error",
        })
      );
    }
  };

  const handleClose = () => {
    setFormData({
      question: {},
      answer: {},
    });
    setIsQuestionValid(true);
    setIsAnswerValid(true);
    onClose();
  };

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      title={
        editingItem
          ? t("eventDetails.faq.modal.editTitle")
          : t("eventDetails.faq.modal.addTitle")
      }
      primaryAction={{
        content: t("common.save"),
        onAction: handleSave,
      }}
      secondaryActions={[
        {
          content: t("common.cancel"),
          onAction: handleClose,
        },
      ]}
    >
      <Modal.Section>
        <BlockStack gap="400">
          <BlockStack gap="300">
            <GenerativeTextField
              id="question-en"
              placementId="event.event.faq.question"
              label={t("eventDetails.faq.modal.questionSection")}
              value={formData.question}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  question: value,
                }))
              }
              type="textField"
              canGenerate={true}
              requiredIndicator
              validateAllLanguages={true}
              onValidationChange={setIsQuestionValid}
            />
          </BlockStack>

          <BlockStack gap="300">
            <GenerativeTextField
              id="answer-en"
              placementId="event.event.faq.answer"
              label={t("eventDetails.faq.modal.answerSection")}
              value={formData.answer}
              onChange={(value) =>
                setFormData((prev) => ({
                  ...prev,
                  answer: value,
                }))
              }
              type="textArea"
              canGenerate={true}
              requiredIndicator
              validateAllLanguages={true}
              onValidationChange={setIsAnswerValid}
            />
          </BlockStack>
        </BlockStack>
      </Modal.Section>
    </Modal>
  );
}
